'use strict';

const { getDependencyClass } = require('../../lib/dependencies.js');
const performanceMeasure = require('../../lib/performanceMeasure.js');
let measure = performanceMeasure('webcomponents:card');
const RECOMMENDATIONS_READY = 'phoenix:recommendations:ready';
const RECOMMENDATIONS_USE_BASE = 'phoenix:recommendations:use_base';
const CARD_MARKUP_API = '/.api/item-card-html/';
const IMAGE_HERO_MARKUP_API = '/.api/image-hero-hub-html/';
const MASK_CLASSNAME = 'is-client-side-rendered';
const LANDSCAPE_CLASSNAME = 'mm-card--landscape-image';
const PORTRAIT_CLASSNAME = 'mm-card--portrait-image';
const ROUND_CLASSNAME = 'mm-card--round-image';
const SQUARE_CLASSNAME = 'mm-card--square-image';
const TEXTONLY_CLASSNAME = 'mm-card--text-only';
const COMPANY_CLASSNAME = 'mm-card--company';
getDependencyClass('phoenix-page').then((pageClass) => {
    /**
     * If PhoenixCard is a child of <phoenix-recommendations>, PhoenixCard is responsible
     * for fetching HTML markup from `/.api/item-card-html/{cardIndex}
     * and replacing skeleton HTML rendered by <phoenix-recommendations>
     * index is the index of the array of content ids returned from the LiftIgniter model server
     *
     * For Image Hero Hub, we fetch from /.api/image-hero-hub-html/{cardIndex} until we refactor
     * Image Hero Hub into a card. See FIXME in cardGroup.js for more detail.
     *
     * If PhoenixCard is used on its own without <phoenix-recommendations>,
     * PhoenixCard is responsible for displaying cards from base content loaders and
     * sending urls to PhoenixRecommendations to avoid duplicate recommendations
     *
     * Documentation: https://liftigniter.readme.io/docs/introduction
     *
     * @example
     *
     *  <phoenix-card
     *      index="3"
     *      config-token="CARD_CONFIG_TOKEN_XXX"
     *  </phoenix-card>
     */
    class PhoenixCard extends pageClass.PhoenixElement {
        get observedBroadcastedEvents() {
            return [RECOMMENDATIONS_READY, RECOMMENDATIONS_USE_BASE];
        }

        get item() {
            if (!this._item) {
                const recommendations = this._parent && this._parent.data;
                if (recommendations && recommendations.length && this.index < recommendations.length) {
                    this._item = recommendations[this.index];
                }
            }
            return this._item;
        }

        set loaded(val) {
            if (val) {
                this._isLoaded = val;
                this._unmask();
                // You can also send any custom event here to notify parent
            }
        }

        get loaded() {
            return this._isLoaded;
        }

        set loading(val) {
            if (val) {
                this._isLoading = val;
            }
        }

        get loading() {
            return this._isLoading;
        }

        connectedCallback() {
            super.connectedCallback();
            this._parent = this.closest('phoenix-recommendations');
            if (this._parent) {
                if (this._parent.useBaseCardOnConnected) {
                    if (!this._parent.isInContentRecommendation) {
                        this._unmask();
                    }
                } else {
                    this.index = parseInt(this.getAttr('index', { required: true }));
                    this.configToken = this.getAttr('config-token', { required: true });
                    this.isImageHeroHub = this.getAttr('image-hero-hub', { required: false });
                }
            } else {
                this.addNonPersonalizedItems();
            }
        }

        broadcastedEventCallback(name, args = {}) {
            const eventValue = (args.detail && args.detail.value) || [];
            if (this._parent && this._parent.widgetName === eventValue) {
                if (name === RECOMMENDATIONS_USE_BASE) {
                    this._unmask();
                    this.addNonPersonalizedItems();
                } else if (name === RECOMMENDATIONS_READY && eventValue && !this.loading && !this.loaded) {
                    this._render();
                }
            }
        }

        addNonPersonalizedItems(url) {
            window.nonPersonalizedItems = window.nonPersonalizedItems || [];
            const link = this.closest('phoenix-super-link');
            if (link && link.href) {
                window.nonPersonalizedItems.push(link.href);
            }
        }

        _render() {
            if (!this.item || this.loaded) {
                return;
            }

            try {
                this.loading = true;
                this._parent.cardRenderedPromises = new Promise((resolve, reject) => {
                    const timeoutPromise = new Promise((resolve, reject) => {
                        setTimeout((values) => {
                            reject('card failed to load in 7 seconds.');
                        }, 7000);
                    });

                    const API_ENDPOINT = `${this.item.siteBasePath || ''}${
                        this.isImageHeroHub ? IMAGE_HERO_MARKUP_API : CARD_MARKUP_API
                    }`;
                    const fetchPromise = fetch(
                        `${API_ENDPOINT}${this.item.channelKey}/${this.item.tempestId}` +
                            `?configToken=${this.configToken}`,
                        {}
                    );

                    // Fetch and replace the markup
                    Promise.race([fetchPromise, timeoutPromise])
                        .then((response) => {
                            if (!this._parent.recordedTimes) {
                                this._parent.recordedTimes = {
                                    cardRenderTimes: {},
                                };
                            }

                            this._parent.recordedTimes.cardRenderTimes[this.index] = Math.trunc(
                                performance.now() - this._parent.recordedTimesForFetching.liModelFetchEndTime
                            ).toString();
                            return response.json();
                        })
                        .then((data) => {
                            if (typeof data.html === 'string' && data.html !== '') {
                                // Replace Card Markup and set isLoaded to true
                                this.innerHTML = data.html;
                                // Replace the href and target for the parent phoenixSuperLink
                                const mainLink = data.mainLink || {};
                                if (mainLink.href) {
                                    const superLink = this.closest('phoenix-super-link');
                                    if (superLink) {
                                        superLink.setAttribute('href', mainLink.href);
                                        superLink.setAttribute('target', mainLink.target || '_self');
                                        superLink.setAttribute(
                                            'onclick',
                                            "return window.phoenixTrackRecommendationClickEvent && phoenixTrackRecommendationClickEvent('cardclick','" +
                                                mainLink.href +
                                                "','" +
                                                this._parent.widgetName +
                                                "',true, 'Image')"
                                        );
                                    }
                                }
                                // If the item originally rendered did not have an image, the phoenix-card
                                // element containing this one will have a text-only class; remove that
                                // if the new item has an image (which should take into account whether
                                // the original component config was text-only), and add a card image modifier
                                // css class
                                if (data.hasImage) {
                                    this.classList.remove(TEXTONLY_CLASSNAME);
                                    if (data.componentImageStyle) {
                                        if (data.componentImageStyle === 'landscape') {
                                            this.classList.add(LANDSCAPE_CLASSNAME);
                                        } else if (data.componentImageStyle === 'portrait') {
                                            this.classList.add(PORTRAIT_CLASSNAME);
                                        } else if (data.componentImageStyle === 'round') {
                                            this.classList.add(ROUND_CLASSNAME);
                                        } else if (data.componentImageStyle === 'square') {
                                            this.classList.add(SQUARE_CLASSNAME);
                                        }
                                    }
                                } else {
                                    this.classList.add(TEXTONLY_CLASSNAME);
                                    if (data.isCompany) {
                                        this.classList.add(COMPANY_CLASSNAME);
                                    }
                                }
                                this._unmask();
                            } else {
                                this._unmask();
                                return resolve(false);
                            }
                            this.loaded = true;
                            resolve(true);
                        })
                        .catch((error) => {
                            // eslint-disable-next-line no-console
                            console.error(error);
                            this.loaded = true;
                            resolve(false);
                        });
                });
            } catch (error) {
                this._unmask();
                // eslint-disable-next-line no-console
                console.error(error);
            }
        }

        _unmask() {
            const link = this.closest('phoenix-super-link');
            if (link) {
                // Remove any masking class from phoenix-super-link
                link.classList.remove(MASK_CLASSNAME);
            }
        }
    }

    window.customElements.define('phoenix-card', PhoenixCard);
    measure.end();
});
